import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace, type Workspace } from "@/hooks/useWorkspace";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Props = { workspace: Workspace };

/**
 * Elite-only per-workspace toggle that flips `profiles.library_isolated`.
 * When ON, this workspace's library is a silo: items are owned by exactly
 * one workspace and the cross-workspace share / chip UI is hidden.
 *
 * Visibility rules:
 *  - Account tier of the **primary** profile must be 'elite' (Agency forces
 *    isolation at the data layer, Plus/Pro don't get the choice).
 *  - Only the plan owner of this workspace can flip it.
 */
export const LibraryIsolationCard = ({ workspace }: Props) => {
  const { user } = useAuth();
  const { workspaces, refresh } = useWorkspace();
  const [saving, setSaving] = useState(false);

  const primaryTier = workspaces.find((w) => w.is_primary)?.account_tier;
  const isElite = primaryTier === "elite";
  const isOwner = !!user && workspace.owner_user_id === user.id;

  if (!isElite || !isOwner) return null;

  const onToggle = async (next: boolean) => {
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ library_isolated: next })
      .eq("id", workspace.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(next ? "Workspace library isolated" : "Workspace library shared");
    refresh();
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Library isolation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          When isolated, this workspace's library is a silo: items belong to one workspace at a time,
          and the cross-workspace share controls are hidden. Useful when this workspace handles
          unrelated content (a separate brand, client, or research project).
        </p>
        <div className="flex items-center justify-between gap-4 rounded-md border border-border bg-muted/30 px-3 py-2.5">
          <Label htmlFor={`isolate-${workspace.id}`} className="cursor-pointer">
            Isolate this workspace's library
          </Label>
          <Switch
            id={`isolate-${workspace.id}`}
            checked={!!workspace.library_isolated}
            onCheckedChange={onToggle}
            disabled={saving}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Existing shared items stay visible in every workspace they were already shared to. Future
          items added here will only live in this workspace.
        </p>
      </CardContent>
    </Card>
  );
};
